import { formatTime, isSameDay, monthGrid } from '@crm/shared';
import { cn } from '@crm/ui';
import type { Appointment } from '../api';
import { STATUS_DOT, VIEW_SURFACE } from './calendar-colors';

const WEEK_DAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'];

/** Quantos eventos cabem na celula antes do "+N". */
const VISIBLE_PER_DAY = 3;

interface MonthGridProps {
  reference: Date;
  appointments: Appointment[];
  onSelectDay: (day: Date) => void;
  onSelect: (id: string) => void;
}

export function MonthGrid({ reference, appointments, onSelectDay, onSelect }: MonthGridProps) {
  const today = new Date();

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border', VIEW_SURFACE)}>
      <div className="grid grid-cols-7 border-b border-border bg-muted/40">
        {WEEK_DAYS.map((label) => (
          <span key={label} className="py-2 text-center text-sm uppercase text-muted-foreground">
            {label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {monthGrid(reference).map((day) => {
          const items = appointments.filter((item) => isSameDay(new Date(item.startAt), day));
          const isOtherMonth = day.getMonth() !== reference.getMonth();
          const isToday = isSameDay(day, today);

          return (
            <div
              key={day.toISOString()}
              className={cn(
                'min-h-24 border-r border-b border-border p-1 last:border-r-0 sm:min-h-28',
                isOtherMonth && 'bg-muted/30',
              )}
            >
              <button
                type="button"
                onClick={() => onSelectDay(day)}
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full text-sm transition-colors hover:bg-muted',
                  isToday && 'bg-primary font-medium text-primary-foreground hover:bg-primary',
                  isOtherMonth && !isToday && 'text-muted-foreground',
                )}
              >
                {day.getDate()}
              </button>

              {/* No mobile so os pontos cabem; no desktop entra o texto. */}
              <div className="mt-1 flex gap-0.5 sm:hidden">
                {items.slice(0, VISIBLE_PER_DAY).map((item) => (
                  <span
                    key={item.id}
                    className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[item.status])}
                  />
                ))}
              </div>

              <div className="mt-1 hidden space-y-0.5 sm:block">
                {items.slice(0, VISIBLE_PER_DAY).map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => onSelect(item.id)}
                    className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-sm transition-colors hover:bg-muted"
                  >
                    <span
                      className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT[item.status])}
                    />
                    <span className="shrink-0 text-muted-foreground">
                      {formatTime(item.startAt)}
                    </span>
                    <span className="truncate">{item.customerName}</span>
                  </button>
                ))}

                {items.length > VISIBLE_PER_DAY ? (
                  <button
                    type="button"
                    onClick={() => onSelectDay(day)}
                    className="px-1 text-sm text-muted-foreground hover:underline"
                  >
                    +{items.length - VISIBLE_PER_DAY} mais
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
